import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchUserProfile,
  updateUserProfile,
  changeUserPassword,
  type UserProfile,
} from '../services/user';
import { Analytics as AdAnalytics } from '../components/admin/Analytics';

const Account: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchUserProfile();
        if (!cancelled) {
          setProfile(data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const raw =
            err &&
            typeof err === 'object' &&
            'message' in err &&
            typeof (err as { message?: unknown }).message === 'string'
              ? ((err as { message?: unknown }).message as string)
              : 'Failed to load profile';

          const normalized = /401|403/.test(raw)
            ? 'Your session has expired or is no longer valid. Please log out and sign in again.'
            : raw;

          setError(normalized);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    setSaving(true);
    setError(null);
    try {
      const updated = await updateUserProfile({
        username: profile.username,
        email: profile.email,
      });
      setProfile(updated);
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message?: unknown }).message === 'string'
          ? ((err as { message?: unknown }).message as string)
          : 'Failed to update profile';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setChangingPassword(true);
    setError(null);
    try {
      await changeUserPassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message?: unknown }).message === 'string'
          ? ((err as { message?: unknown }).message as string)
          : 'Failed to change password';
      setError(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  const accountStatusLabel = (() => {
    if (!profile) return '';
    switch (profile.accountStatus) {
      case 'good':
        return 'In good standing';
      case 'past_due':
        return 'Past due';
      case 'suspended':
        return 'Suspended';
      default:
        return profile.accountStatus;
    }
  })();

  return (
    <main className="mx-auto max-w-3xl px-4 py-8">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">My account</h1>
        <div className="flex items-center gap-2">
          <Link
            to="/campaigns"
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            My campaigns
          </Link>
          <Link
            to="/"
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Go home
          </Link>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-500 dark:text-slate-400">Loading your profile…</p>}
      {error && !loading && <p className="text-sm text-red-600">{error}</p>}

      {profile && !loading && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Profile</h2>
                <div className="mt-2 space-y-3 text-sm text-slate-800 dark:text-slate-200">
                  <label className="block">
                    <span className="block text-xs text-slate-500 dark:text-slate-400">Username</span>
                    <input
                      type="text"
                      value={profile.username}
                      onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-xs text-slate-500 dark:text-slate-400">Email</span>
                    <input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-400">
                    <span>
                      Role: <span className="font-medium capitalize">{profile.role}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate('/payment')}
                      className="inline-flex items-center rounded-md border border-blue-600 px-2.5 py-1 font-medium text-blue-600 hover:bg-blue-50"
                    >
                      Go to payments
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
                <div>
                  <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Account status</h2>
                  <p className="mt-1 text-sm text-slate-800 dark:text-slate-200">{accountStatusLabel}</p>
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Change password</h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Update the password you use to sign in to TELLURVISION.
            </p>
            <form onSubmit={handleChangePassword} className="mt-3 space-y-3 text-sm text-slate-800 dark:text-slate-200">
              <label className="block">
                <span className="block text-xs text-slate-500 dark:text-slate-400">Current password</span>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-slate-500 dark:text-slate-400">New password</span>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-slate-500 dark:text-slate-400">Confirm new password</span>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <div className="flex justify-end pt-1">
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {changingPassword ? 'Updating…' : 'Change password'}
                </button>
              </div>
            </form>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-300">My ad analytics</h2>
            <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
              Performance for ads associated with your account. Use this to see how many views,
              clicks, and safety blanks your content is generating.
            </p>
            <AdAnalytics userId={profile.id} title="My ad analytics" />
          </div>
        </div>
      )}
    </main>
  );
};

export default Account;
